import type { CaminoMapa, Mapa, MuelleMapa, Punto, Sector } from '../api';
import { useEsCelular } from '../lib/useMedia';
import {
  LLENADO,
  botonSecundario,
  c,
  colorAbertura,
  colorCamino,
  colorSector,
  COLOR_MURO,
  nota,
} from '../theme';

/**
 * El plano en planta de Ubicaciones. Vivía dentro de `views/Racks.tsx`, que
 * había pasado las mil líneas; salió acá en la etapa 3, que es la que vuelve a
 * tocar ese archivo para montar la escena 3D.
 *
 * **`Racks.tsx` lo sigue reexportando, y eso no es comodidad:**
 * `views/plano.check.tsx` y `demo-plano.tsx` lo importan desde `./Racks`, y
 * ese import es lo que hace que `npm run check:plano` construya por SSR el
 * módulo entero de `Racks.tsx` —la red que vigila que el chunk de Three siga
 * cargándose diferido—. Si el check apuntara directo a este archivo, esa red
 * desaparecería sin que nadie lo note.
 */
/** Color de una posición según cuántos de sus niveles están ocupados. */
function llenado(ocupados: number, niveles: number): string {
  if (ocupados === 0) return LLENADO[0]!;
  const paso = Math.ceil((ocupados / Math.max(niveles, 1)) * 3);
  return LLENADO[Math.min(3, Math.max(1, paso))]!;
}

/** Con qué se pinta un muro: un solo tono gris para todo material, porque acá
 * lo que importa distinguir es la abertura, no de qué está hecha la pared. */


// Con qué se pinta cada tipo de abertura: una sola tabla para toda la app,
// en `theme.ts` (`COLOR_ABERTURA`/`colorAbertura`), junto a `COLOR_CAMINO`.

/**
 * El rectángulo con el que se dibuja un tramo de muro o el hueco de una
 * abertura: centrado en el segmento `a`-`b`, con el espesor real a los dos
 * lados. Los muros de hoy son siempre horizontales o verticales, pero el giro
 * deja bien cualquier ángulo por si el día de mañana se traza uno al sesgo.
 */
function cajaDelTramo(a: Punto, b: Punto, espesorM: number) {
  const largo = Math.hypot(b.x - a.x, b.y - a.y);
  const angulo = (Math.atan2(b.y - a.y, b.x - a.x) * 180) / Math.PI;
  const cx = (a.x + b.x) / 2;
  const cy = (a.y + b.y) / 2;
  return {
    x: cx - largo / 2,
    y: cy - espesorM / 2,
    ancho: largo,
    alto: espesorM,
    transform: `rotate(${angulo} ${cx} ${cy})`,
  };
}

/**
 * Plano de la nave a escala, con la geometría real: la misma que dibuja el
 * editor de planta, pero pintada por ocupación en vez de por sector. Se usa
 * SVG con metros como unidad, así que lo que se guarda en metros se dibuja en
 * metros sin conversiones que puedan quedar desfasadas.
 */
// Exportado para el banco de pruebas de /demo-plano.html, que lo monta con
// datos armados a mano para poder mirar el plano sin base de datos.
export function PlanoGeneral({
  predio,
  naves,
  sectores,
  muelles,
  caminos,
  onSector,
}: {
  predio: Mapa['predio'];
  naves: Mapa['naves'];
  sectores: Sector[];
  muelles: MuelleMapa[];
  caminos: CaminoMapa[];
  onSector: (id: string) => void;
}) {
  const esCelular = useEsCelular();
  const ubicados = sectores.filter((s) => s.area !== null);
  const sinPlano = sectores.filter((s) => s.area === null);
  const muellesSinUbicar = muelles.filter((m) => m.caja === null);

  const { ancho, largo } = predio;
  const margen = 2;

  return (
    <div>
      <svg
        viewBox={`${-margen} ${-margen} ${ancho + margen * 2} ${largo + margen * 2}`}
        style={{
          width: '100%',
          height: 'auto',
          maxHeight: 'calc(100vh - 260px)',
          display: 'block',
          background: c.fondo,
          borderRadius: 12,
          border: `1px solid ${c.borde}`,
        }}
        role="img"
        aria-label="Plano del depósito"
      >
        <defs>
          <pattern id="grilla-mapa" width="1" height="1" patternUnits="userSpaceOnUse">
            <path d="M 1 0 L 0 0 0 1" fill="none" stroke={c.borde} strokeWidth="0.03" />
          </pattern>
        </defs>

        {/*
          Las naves, y todo lo que viene de `/mapa`, ya llegan en coordenadas
          del predio: el backend le sumó el rincón de su nave a cada caja. Acá
          se dibuja tal cual, sin trasladar nada. El que traslada es el editor
          (`/planta`), que recibe coordenadas de la nave porque escribe de
          vuelta y lo guardado es relativo a la nave.
        */}
        {naves.map((n) => (
          <g key={n.id}>
            <title>{`${n.nombre} · ${n.caja.ancho} × ${n.caja.alto} m`}</title>
            <rect
              x={n.caja.x}
              y={n.caja.y}
              width={n.caja.ancho}
              height={n.caja.alto}
              fill="#fff"
              stroke={c.bordeFuerte}
              strokeWidth={0.18}
            />
            <rect x={n.caja.x} y={n.caja.y} width={n.caja.ancho} height={n.caja.alto} fill="url(#grilla-mapa)" />
            {/*
              El nombre va debajo de la nave, afuera de la planta: apoyado en
              el rincón noroeste -donde estaba- caía justo encima de lo que
              suele haber en un rincón, que en la demo es una oficina de 4×4 m.
              Afuera no se monta con nada, y el margen del encuadre (2 m) deja
              lugar de sobra para el renglón.
            */}
            <text
              x={n.caja.x + 0.4}
              y={n.caja.y + n.caja.alto + 1.3}
              fontSize={1.1}
              fill={c.texto3}
              style={{ fontWeight: 600 }}
            >
              {n.nombre}
            </text>
          </g>
        ))}

        {/* Caminos y muelles: pintura de piso, todo lo demás va encima */}
        {caminos.map((k) => {
          const paleta = colorCamino(k.tipo);
          return (
            <g key={`k${k.id}`}>
              <title>{k.nombre}</title>
              <rect
                x={k.caja.x}
                y={k.caja.y}
                width={k.caja.ancho}
                height={k.caja.alto}
                fill={paleta.relleno}
                stroke={paleta.borde}
                strokeWidth={0.06}
                rx={0.2}
              />
            </g>
          );
        })}

        {muelles.map((m) =>
          m.caja ? (
            <g key={m.id}>
              <title>{`Muelle ${m.id}`}</title>
              <rect
                x={m.caja.x}
                y={m.caja.y}
                width={m.caja.ancho}
                height={m.caja.alto}
                fill="#dfe4ea"
                stroke={c.bordeFuerte}
                strokeWidth={0.06}
                rx={0.15}
              />
            </g>
          ) : null,
        )}

        {/*
          Muros: su propia capa, arriba de los caminos y abajo de los racks.
          Los tramos ya vienen con los huecos de las aberturas descontados
          -es el backend el que resta- así que acá se dibuja cada tramo tal
          cual, sin volver a restarle nada. Las coordenadas son del predio,
          igual que las naves: no se les suma el rincón de la nave de nuevo.
        */}
        {naves.flatMap((n) =>
          n.muros.flatMap((m) => [
            ...m.tramos.map((t, i) => {
              const r = cajaDelTramo(t.a, t.b, m.espesor_m);
              return (
                <rect
                  key={`${m.clave}-t${i}`}
                  x={r.x}
                  y={r.y}
                  width={r.ancho}
                  height={r.alto}
                  transform={r.transform}
                  fill={COLOR_MURO.relleno}
                  stroke={COLOR_MURO.borde}
                  strokeWidth={0.03}
                />
              );
            }),
            // La abertura se pinta con el color de su tipo, encima del tramo:
            // en un portón no hay tramo debajo (el hueco llega al piso), pero
            // en un ventanal con antepecho sí lo hay, y esto marca la altura
            // a la que se abre sin tener que dibujar el dintel aparte.
            ...m.aberturas.map((ab) => {
              const r = cajaDelTramo(ab.a, ab.b, m.espesor_m);
              const color = colorAbertura(ab.tipo);
              return (
                <g key={`${m.clave}-a${ab.id}`}>
                  <title>{`${ab.nombre || ab.tipo} · alto ${ab.alto_m} m`}</title>
                  <rect
                    x={r.x}
                    y={r.y}
                    width={r.ancho}
                    height={r.alto}
                    transform={r.transform}
                    fill={color}
                    fillOpacity={0.55}
                    stroke={color}
                    strokeWidth={0.03}
                  />
                </g>
              );
            }),
          ]),
        )}

        {/* Sectores: el área que envuelve a sus racks. El nombre va aparte, ver abajo */}
        {ubicados.map((s) => {
          const color = colorSector(s.color, sectores.indexOf(s));
          const a = s.area!;
          return (
            <g key={s.id} onClick={() => onSector(s.id)} style={{ cursor: 'pointer' }}>
              {/* Una sola cadena: un <title> con varios hijos sale con los
                  comentarios de React adentro del globito. */}
              <title>{`${s.nombre} · ${s.uso} · ${s.pct}% ocupado`}</title>
              <rect
                x={s.dibujada ? a.x : a.x - 0.6}
                y={s.dibujada ? a.y : a.y - 0.6}
                width={s.dibujada ? a.ancho : a.ancho + 1.2}
                height={s.dibujada ? a.alto : a.alto + 1.2}
                fill={s.hits ? c.ambarSuave : color}
                fillOpacity={s.hits ? 0.35 : s.dibujada ? 0.1 : 0.05}
                stroke={s.hits ? c.ambar : color}
                strokeOpacity={s.hits ? 0.9 : 0.35}
                strokeWidth={0.1}
                strokeDasharray="0.5 0.4"
                rx={0.5}
                // Al buscar, el sector que coincide se tiñe de a poco. Sólo
                // color: las medidas no, que con `transition` en x/y/width el
                // dibujo se arrastraría detrás de cada refresco.
                style={{ transition: 'fill .35s, fill-opacity .35s, stroke .35s, stroke-opacity .35s' }}
              />
              {/* Cada posición del rack, pintada por lo que tiene adentro */}
              {s.racks.map((r) =>
                r.posiciones.map((p) =>
                  p.caja ? (
                    <rect
                      key={p.codigo}
                      x={p.caja.x}
                      y={p.caja.y}
                      width={p.caja.ancho}
                      height={p.caja.alto}
                      fill={p.hit ? c.ambarSuave : llenado(p.ocupados, r.niveles)}
                      stroke={p.hit ? c.ambar : p.ocupados ? c.verde : c.bordeFuerte}
                      strokeWidth={0.04}
                      vectorEffect="non-scaling-stroke"
                      style={{ transition: 'fill .35s, stroke .35s' }}
                    />
                  ) : null,
                ),
              )}
            </g>
          );
        })}

        {/*
          ---------------------------------------------------------------------
          LOS RÓTULOS, ADENTRO DE SU SECTOR Y EN UNA CAPA APARTE
          ---------------------------------------------------------------------
          Tenían dos defectos que se venían juntos y que en el depósito real
          —ocho sectores apilados— hacían el plano ilegible (reportado con
          captura, 2026-09-07):

          1. **Iban arriba del área** (`y = tope − 0.5`), o sea **afuera** del
             rectángulo que nombran. Con los sectores pegados uno abajo del
             otro, el rótulo de cada uno caía **adentro del sector de encima**:
             ocho nombres flotando entre bandas ajenas, imposible saber cuál
             nombra a cuál.
          2. **Quedaban debajo de los racks.** Cada sector dibuja sus
             posiciones dentro de su propio `<g>`, y el `<g>` del sector
             siguiente se dibuja después: en SVG eso es "más arriba". Así que
             el rótulo —que está en la banda de al lado— lo tapaba el rack del
             vecino, y en pantalla se leía «Sec».

          Los dos se arreglan de la misma forma: el rótulo va **adentro** de su
          rectángulo, arriba a la izquierda, y **en una pasada aparte, después
          de todos los sectores**, así nada lo puede tapar. El halo blanco
          (`paintOrder: stroke`, que dibuja el contorno **detrás** de la letra)
          es lo que lo deja legible tanto sobre el tinte del sector como sobre
          el blanco de una posición vacía, sin tener que ponerle un cartel.

          Sigue siendo clickeable y sigue entrando al sector: es el mismo
          `onSector` del área.
        */}
        {ubicados.map((s) => {
          const color = colorSector(s.color, sectores.indexOf(s));
          const a = s.area!;
          // El rectángulo tal como se dibujó arriba: el del sector sin zona
          // propia sale inflado 0,6 m por lado, y el rótulo tiene que ir
          // adentro **de ese**, no del área cruda.
          const x0 = s.dibujada ? a.x : a.x - 0.6;
          const y0 = s.dibujada ? a.y : a.y - 0.6;
          const ancho = s.dibujada ? a.ancho : a.ancho + 1.2;
          const alto = s.dibujada ? a.alto : a.alto + 1.2;

          // El cuerpo se achica si el sector es una banda muy finita, para no
          // asomar por abajo del rectángulo que lo contiene.
          const cuerpo = Math.min(esCelular ? 2.4 : 1.1, alto * 0.55);
          const sangria = cuerpo * 0.4;
          const marca = s.hits ? (s.hits === 1 ? '1 coincide' : `${s.hits} coinciden`) : `${s.pct}%`;
          // Ancho aproximado en metros. 0,55 em por carácter es lo que mide a
          // ojo una tipografía de interfaz, y alcanza para decidir si entra:
          // si no entra, se cae el porcentaje antes que el nombre, porque el
          // porcentaje lo repite el globito y el nombre no.
          const entra = (t: string) => t.length * cuerpo * 0.55 <= ancho - sangria * 2;
          const conMarca = entra(`${s.nombre} · ${marca}`);

          return (
            <g key={`rotulo-${s.id}`} onClick={() => onSector(s.id)} style={{ cursor: 'pointer' }}>
              <text
                x={x0 + sangria}
                y={y0 + cuerpo * 1.05}
                fontSize={cuerpo}
                fontWeight={600}
                fill={color}
                stroke={c.panel}
                strokeWidth={cuerpo * 0.3}
                strokeOpacity={0.85}
                strokeLinejoin="round"
                paintOrder="stroke"
              >
                {s.nombre}
                {conMarca && (
                  <tspan fill={c.texto3} fontWeight={400}>
                    {' '}
                    · {marca}
                  </tspan>
                )}
              </text>
            </g>
          );
        })}

        {ubicados.length === 0 && (
          <text
            x={ancho / 2}
            y={largo / 2}
            textAnchor="middle"
            fontSize={Math.max(ancho, largo) / 34}
            fill={c.texto3}
          >
            La planta todavía no está dibujada.
          </text>
        )}
      </svg>

      {(sinPlano.length > 0 || muellesSinUbicar.length > 0) && (
        <div style={{ ...nota, marginTop: 12 }}>
          {sinPlano.length > 0 && (
            <>
              Fuera del plano: {sinPlano.map((s) => s.nombre).join(', ')}. Se consultan desde estos accesos, pero no
              aparecen dibujados hasta que sus racks tengan posición.
              <span style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
                {sinPlano.map((s) => (
                  <button key={s.id} type="button" onClick={() => onSector(s.id)} style={botonSecundario}>
                    {s.nombre}
                  </button>
                ))}
              </span>
            </>
          )}
          {muellesSinUbicar.length > 0 && (
            <div style={{ marginTop: sinPlano.length ? 8 : 0 }}>
              Muelles sin ubicar: {muellesSinUbicar.map((m) => m.id).join(', ')}.
            </div>
          )}
        </div>
      )}

      <div style={{ marginTop: 14, display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 12.5, color: c.texto2 }}>
        {/*
          La ocupación de las posiciones y, atrás, el vocabulario de los
          cerramientos: sin esto los cuatro colores de abertura no se pueden
          leer -nada dice que naranja es un portón y celeste un ventanal- y
          los ventanales contra el borde no se distinguían de un adorno. Los
          colores salen de la misma tabla con la que se dibuja
          (`colorAbertura` y `COLOR_MURO`, las dos en `theme.ts`), y la muestra
          lleva la misma transparencia que el hueco en el plano, así que si la
          tabla cambia la leyenda cambia con ella.
        */}
        {[
          { t: 'Posición libre', bg: LLENADO[0], bd: c.bordeFuerte },
          { t: 'Parcial', bg: LLENADO[2], bd: c.verde },
          { t: 'Completa', bg: LLENADO[3], bd: c.verde },
          { t: 'Coincide con la búsqueda', bg: c.ambarSuave, bd: c.ambar },
          { t: 'Muro', bg: COLOR_MURO.relleno, bd: COLOR_MURO.borde },
          { t: 'Portón', bg: colorAbertura('porton'), bd: colorAbertura('porton'), op: 0.55 },
          { t: 'Puerta', bg: colorAbertura('peatonal'), bd: colorAbertura('peatonal'), op: 0.55 },
          { t: 'Ventanal', bg: colorAbertura('ventanal'), bd: colorAbertura('ventanal'), op: 0.55 },
          { t: 'Paso', bg: colorAbertura('paso'), bd: colorAbertura('paso'), op: 0.55 },
        ].map((l) => (
          <span key={l.t} style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
            <span
              style={{
                width: 14,
                height: 10,
                borderRadius: 3,
                background: l.bg,
                border: `1px solid ${l.bd}`,
                opacity: l.op ?? 1,
              }}
            />
            {l.t}
          </span>
        ))}
        <span
          style={{
            // En pantalla angosta va en su propio renglón: apretada contra el
            // borde derecho quedaba partida en tres líneas desparejas.
            flex: esCelular ? '1 1 100%' : 1,
            minWidth: 0,
            textAlign: esCelular ? 'left' : 'right',
            color: c.texto3,
          }}
        >
          {/* `ancho` y `largo` son del predio, no de una nave: son varias y
              cada una lleva su medida en su propio globito. Decía «Nave de 60
              × 40 m» cuando las naves eran de 34×40 y 26×40. */}
          Predio de {ancho} × {largo} m · tocá un sector para entrar
        </span>
      </div>
    </div>
  );
}
